import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../helpers/format';
import Dropdown from '../Dropdown/Dropdown';
import Table from '../Table/Table';
import style from './CompareStocks.module.css';
import StockImg from '../StockImg/StockImg';

const CompareStocks = ({ stock, metricPercentiles }) => {
  const [customColumn, setCustomColumn] = useState();
  const [showPeers, setShowPeers] = useState(true); // change to false to hide similar companies by default
  const [isLoading, setIsLoading] = useState(false);
  const [peers, setPeers] = useState();

  // set custom column from local storage on mount
  useEffect(() => {
    const savedCustomColumn = localStorage.getItem('customColumn');
    if (savedCustomColumn) {
      setCustomColumn(savedCustomColumn);
    }
  }, []);

  // save custom column to local storage on change
  useEffect(() => {
    if (customColumn) {
      localStorage.setItem('customColumn', customColumn);
    } else {
      localStorage.removeItem('customColumn');
    }
  }, [customColumn]);

  // load peers on showPeers change
  useEffect(() => {
    if (showPeers) {
      setIsLoading(true);
      fetchPeers(stock).then(data => {
        setPeers(data);
        setIsLoading(false);
      });
    }
  }, [showPeers]);

  return (
    <div>
      <h3 className={style.title}>Compare to Similar Companies</h3>

      <div className={style.scrollContainer}>
        <Table>
          <thead>
            <tr>
              <th></th>
              <th>P/E Ratio</th>
              <th>Dividends</th>
              <th>Return on Equity</th>
              <th>Price-to-Sales</th>
              <th>Debt-to-Equity</th>
              <th width={customColumn ? 'auto' : '120px'}>
                <Dropdown options={customColumnOptions(setCustomColumn)}>
                  <span className={style.customColumnHeader}>
                    {customColumnOptions(setCustomColumn).find((option) => option.value === customColumn)?.name || '+ Add Column'}
                  </span>
                </Dropdown>
                {customColumn &&
                  <span>
                    <button onClick={() => setCustomColumn()} className={style.customColumnRemove}>
                      <img src="/react/phosphor/x-bold.svg" alt="Remove" width="10" height="10" />
                    </button>
                  </span>
                }
              </th>
            </tr>
          </thead>
          <tbody>
            <Row stock={stock} customColumn={customColumn} />
            {/* <MetricPercentileRows metrics={metricPercentiles} customColumn={customColumn} /> */}
            {<PeersAvgRow customColumn={customColumn} peers={peers} />}
            {(showPeers && !isLoading && peers !== undefined) ?
              peers.length > 0 && peers.map((peer) => <Row key={peer.id} stock={peer} customColumn={customColumn} />)
              :
              <tr>
                <th className={style.icon}>
                  <button onClick={() => setShowPeers(true)} className={style.peerName}>
                    {showPeers ?
                      <img src="/react/phosphor/circle-notch-duotone.svg" width="20" height="20" alt="Show Similar Companies" className={`${style.logo} ${style.spinning}`} />
                      :
                      <img src="/react/phosphor/plus-circle-duotone.svg" width="20" height="20" alt="Show Similar Companies" className={style.logo} />
                    }
                    <span>Similar Companies</span>
                  </button>
                </th>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
                <td><button onClick={() => setShowPeers(true)}>&nbsp;</button></td>
              </tr>
            }
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default CompareStocks;

function Row({ stock, customColumn }) {
  return (
    <tr>
      <th>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`} className={style.peerName}>
          <StockImg symbol={stock.symbol} alt={stock.name} width="24" height="24" className={style.logo} />
          <span className={style.peerNameText}>{stock.name}</span>
        </a>
      </th>
      <td>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
          {Math.round(stock.pe_ratio * 10) / 10}
        </a>
      </td>
      <td>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
          {stock.dividend_yield ?
            `${Math.round(stock.dividend_yield * 100 * 100) / 100}%`
            : <NotAvailable />}
        </a>
      </td>
      <td>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
          {stock.return_on_equity ?
            `${Math.round(stock.return_on_equity * 100)}%`
            : <NotAvailable />}
        </a>
      </td>
      <td>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
          {Math.round(stock.price_to_sales * 10) / 10 || <NotAvailable />}
        </a>
      </td>
      <td>
        <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
          {Math.round(stock.debt_to_equity * 10) / 10 || <NotAvailable />}
        </a>
      </td>
      <td>
        {customColumn &&
          <a href={`/stocks/${stock.symbol.toLowerCase()}`}>
            {formatCustomColumn(customColumn, stock[customColumn]) || <NotAvailable />}
          </a>
        }
      </td>
    </tr>
  );
}

function PeersAvgRow({ customColumn, peers }) {
  if (peers === undefined || peers.length <= 1) {
    return null;
  }

  // calculate average of all peers, make sure to exclude the peers where that one metric is not available
  const avgPeers = peers.reduce((acc, peer) => {
    if (peer.pe_ratio) {
      acc.pe_ratio_avg.push(parseFloat(peer.pe_ratio));
    }
    if (peer.dividend_yield) {
      acc.dividend_yield_avg.push(parseFloat(peer.dividend_yield));
    }
    if (peer.return_on_equity) {
      acc.return_on_equity_avg.push(parseFloat(peer.return_on_equity));
    }
    if (peer.price_to_sales) {
      acc.price_to_sales_ratio_avg.push(parseFloat(peer.price_to_sales));
    }
    if (peer.debt_to_equity) {
      acc.debt_to_equity_avg.push(parseFloat(peer.debt_to_equity));
    }
    if (customColumn && peer[customColumn]) {
      acc[customColumn + '_avg'].push(parseFloat(peer[customColumn]));
    }
    return acc;
  }, {
    pe_ratio_avg: [],
    dividend_yield_avg: [],
    return_on_equity_avg: [],
    price_to_sales_ratio_avg: [],
    debt_to_equity_avg: [],
    [customColumn + '_avg']: []
  });

  // calculate average
  avgPeers.pe_ratio_avg = avgPeers.pe_ratio_avg.reduce((a, b) => a + b, 0) / avgPeers.pe_ratio_avg.length;
  avgPeers.dividend_yield_avg = avgPeers.dividend_yield_avg.reduce((a, b) => a + b, 0) / avgPeers.dividend_yield_avg.length;
  avgPeers.return_on_equity_avg = avgPeers.return_on_equity_avg.reduce((a, b) => a + b, 0) / avgPeers.return_on_equity_avg.length;
  avgPeers.price_to_sales_ratio_avg = avgPeers.price_to_sales_ratio_avg.reduce((a, b) => a + b, 0) / avgPeers.price_to_sales_ratio_avg.length;
  avgPeers.debt_to_equity_avg = avgPeers.debt_to_equity_avg.reduce((a, b) => a + b, 0) / avgPeers.debt_to_equity_avg.length;
  avgPeers[customColumn + '_avg'] = avgPeers[customColumn + '_avg'].reduce((a, b) => a + b, 0) / avgPeers[customColumn + '_avg'].length;

  return (
    <tr>
      <th className={style.icon}>
        <img src="/react/phosphor/factory-duotone.svg" width="20" height="20" alt="Similar Companies Avg" className={style.logo} />
        <span>Similar Companies Avg</span>
      </th>
      <td>
        {Math.round(avgPeers.pe_ratio_avg * 10) / 10}
      </td>
      <td>
        {avgPeers.dividend_yield_avg ?
          `${Math.round(avgPeers.dividend_yield_avg * 100 * 100) / 100}%`
          : <NotAvailable />}
      </td>
      <td>
        {avgPeers.return_on_equity_avg ?
          `${Math.round(avgPeers.return_on_equity_avg * 100)}%`
          : <NotAvailable />}
      </td>
      <td>
        {Math.round(avgPeers.price_to_sales_ratio_avg * 10) / 10 || <NotAvailable />}
      </td>
      <td>
        {Math.round(avgPeers.debt_to_equity_avg * 10) / 10 || <NotAvailable />}
      </td>
      <td>
        {customColumn &&
          (formatCustomColumn(customColumn, avgPeers[customColumn + '_avg']) || <NotAvailable />)
        }
      </td>
    </tr>
  );
}

function MetricPercentileRows({ metrics, customColumn }) {
  return metrics.map(metric => {
    return (
      <tr key={metric.sector}>
        <th className={style.icon}>
          <img src={metric.sector === 'market' ? '/react/phosphor/storefront-duotone.svg' : '/react/phosphor/factory-duotone.svg'} width="20" height="20" alt={metric.sector} className={style.logo} />
          <span>{capitalizeFirstLetter(metric.sector)} Avg</span>
        </th>
        <td>
          {Math.round(metric.pe_ratio_avg * 10) / 10}
        </td>
        <td>
          {metric.dividend_yield_avg ?
            `${Math.round(metric.dividend_yield_avg * 100 * 100) / 100}%`
            : <NotAvailable />}
        </td>
        <td>
          {metric.return_on_equity_avg ?
            `${Math.round(metric.return_on_equity_avg * 100)}%`
            : <NotAvailable />}
        </td>
        <td>
          {Math.round(metric.price_to_sales_ratio_avg * 10) / 10 || <NotAvailable />}
        </td>
        <td>
          {Math.round(metric.debt_to_equity_avg * 10) / 10 || <NotAvailable />}
        </td>
        <td>
          {customColumn &&
            (formatCustomColumn(customColumn, metric[customColumn + '_avg']) || <NotAvailable />)
          }
        </td>
      </tr>
    );
  });
}

function NotAvailable() {
  return (
    <span className={style.notAvailable}>-</span>
  );
}

function fetchPeers(stock) {
  return fetch('/react/stocks/peers', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ stock_id: stock.id })
  })
    .then(response => response.json());
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function formatCustomColumn(column, value) {
  if (column === 'one_year_return') {
    return `${Math.round(value * 100)}%`;
  } else if (column === 'peg') {
    return Math.round(value * 10) / 10;
  } else if (column === 'price_to_book_ratio') {
    return Math.round(value * 10) / 10;
  } else if (column === 'revenue_to_enterprise_value') {
    return Math.round(value * 10) / 10;
  } else if (column === 'ebit_to_enterprise_value') {
    return Math.round(value * 10) / 10;
  } else if (column === 'total_debt_to_enterprise_value') {
    return Math.round(value * 10) / 10;
  } else if (column === 'total_revenue') {
    return `$${formatCurrency(value, { compact: true })}`;
  } else if (column === 'ebit') {
    return `$${formatCurrency(value, { compact: true })}`;
  } else if (column === 'net_income') {
    return `$${formatCurrency(value, { compact: true })}`;
  } else if (column === 'profit_margin') {
    return `${Math.round(value * 100 * 10) / 10}%`;
  } else if (column === 'earnings_growth') {
    return `${Math.round(value * 100 * 10) / 10}%`;
  } else if (column === 'return_on_assets') {
    return `${Math.round(value * 100)}%`;
  } else if (column === 'return_on_invested_capital') {
    return `${Math.round(value * 100)}%`;
  }
}

function customColumnOptions(setCustomColumn) {
  return [
    { name: 'One Year Return', value: 'one_year_return', onClick: () => setCustomColumn('one_year_return') },
    { name: 'PEG', value: 'peg', onClick: () => setCustomColumn('peg') },
    { name: 'Price-to-Book', value: 'price_to_book_ratio', onClick: () => setCustomColumn('price_to_book_ratio') },
    { name: 'Revenue-to-Enterprise Value', value: 'revenue_to_enterprise_value', onClick: () => setCustomColumn('revenue_to_enterprise_value') },
    { name: 'EBIT-to-Enterprise Value', value: 'ebit_to_enterprise_value', onClick: () => setCustomColumn('ebit_to_enterprise_value') },
    { name: 'Total Debt-to-Enterprise Value', value: 'total_debt_to_enterprise_value', onClick: () => setCustomColumn('total_debt_to_enterprise_value') },
    { name: 'Total Revenue TTM', value: 'total_revenue', onClick: () => setCustomColumn('total_revenue') },
    { name: 'EBIT TTM', value: 'ebit', onClick: () => setCustomColumn('ebit') },
    { name: 'Net Income TTM', value: 'net_income', onClick: () => setCustomColumn('net_income') },
    { name: 'Profit Margin TTM', value: 'profit_margin', onClick: () => setCustomColumn('profit_margin') },
    { name: 'Earnings Growth YOY', value: 'earnings_growth', onClick: () => setCustomColumn('earnings_growth') },
    { name: 'Return on Assets', value: 'return_on_assets', onClick: () => setCustomColumn('return_on_assets') },
    { name: 'Return on Invested Capital', value: 'return_on_invested_capital', onClick: () => setCustomColumn('return_on_invested_capital') },
  ];
}